/* Deep links between a product page and the measuring guide.
   On a product page these params are honoured:
     ?variant=<id>   exact variant (Dawn, wins over the readable params below)
     ?color=<name>   e.g. Sage Green — matched loosely, applied here
     ?width=52&length=96   nearest available size (ona-size-split.js)
     ?quantity=2     (ona-purchase.js)
   Every link to the measuring guide is kept in step with the live selection, so a
   guide opened from a product page arrives with that curtain, colour, size and
   quantity already set. Links keep their normal behaviour, including target="_blank"
   and modifier-clicks: the href is rewritten, the navigation is not hijacked. */
(() => {
  'use strict';
  const GUIDE = '/pages/curtain-measuring-guide';
  const norm = value => String(value == null ? '' : value).trim().toLowerCase().replace(/[^a-z0-9]+/g, '');
  const handleOf = () => (location.pathname.match(/^\/products\/([^/?#]+)/) || [])[1] || '';
  const sizeOf = value => {
    const match = String(value || '').match(/([\d.]+)\s*[″"]?\s*W\s*[×x]\s*([\d.]+)/i);
    return match ? { width: match[1], length: match[2] } : null;
  };

  function selection() {
    const handle = handleOf();
    if (!handle) return null;
    const info = document.querySelector('product-info') || document;
    const query = new URLSearchParams(location.search);
    const size = sizeOf(info.querySelector('select[name="options[Size]"]')?.value);
    const quantity = info.querySelector('input[name="quantity"]')?.value;
    return {
      product: handle,
      variant: info.querySelector('form[action*="/cart/add"] input[name="id"]')?.value || query.get('variant') || '',
      color: info.querySelector('input[type="radio"][data-option-name="Color"]:checked')?.value
        || info.querySelector('[data-ona-color-name]')?.textContent.trim() || '',
      width: size?.width || query.get('width') || '',
      length: size?.length || query.get('length') || '',
      quantity: Number(quantity) > 1 ? String(Number(quantity)) : ''
    };
  }

  // Returns the guide URL carrying the current selection, or null when `from` is not a guide link.
  function guideURL(from) {
    let url;
    try { url = new URL(from || GUIDE, location.origin); } catch { return null; }
    if (url.origin !== location.origin || !url.pathname.startsWith(GUIDE)) return null;
    const current = selection();
    if (!current) return url;
    for (const [key, value] of Object.entries(current)) {
      if (value) url.searchParams.set(key, value); else url.searchParams.delete(key);
    }
    return url;
  }

  function refresh() {
    if (!handleOf()) return;
    document.querySelectorAll('a[href]').forEach(link => {
      const href = link.getAttribute('href');
      if (!href || href.indexOf(GUIDE) === -1) return;
      const url = guideURL(href);
      if (url) link.setAttribute('href', url.pathname + url.search);
    });
  }

  // An explicit ?variant= wins; ?color= is for readable, shareable links.
  let colourApplied = false;
  function applyColor() {
    if (colourApplied || !handleOf()) return;
    const query = new URLSearchParams(location.search);
    const wanted = query.get('color') || query.get('shade');
    if (!wanted || query.has('variant')) { colourApplied = true; return; }
    const info = document.querySelector('product-info');
    const choices = [...(info?.querySelectorAll('input[type="radio"][data-option-name="Color"]') || [])];
    if (!choices.length) return;
    colourApplied = true;
    const target = norm(wanted);
    const hit = choices.find(choice => norm(choice.value) === target)
      || choices.find(choice => norm(choice.value).startsWith(target))
      || choices.find(choice => target && norm(choice.value).indexOf(target) !== -1);
    if (hit && !hit.checked && !hit.disabled) hit.click(); // Dawn owns the re-render
  }


  /* ---- measuring guide side -------------------------------------------
     The guide already reads ?product and ?variant (style + colour) and
     ?span/?drop/?fullness/?finish. These add the readable params, so a guide
     opened from a product page arrives on that curtain, colour and length:
       ?color=   picks the example colour when no variant matched
       ?length=  prefills the drop, which is the length the shopper chose
       ?quantity= is carried back to the product page
     Left alone on purpose: the rod width. It is the shopper's window, not
     something the product page knows, so it is never pre-filled from a link.
     Runs after the guide's own init and only fills fields it finds empty. */
  function hydrateGuide() {
    if (!location.pathname.startsWith(GUIDE)) return;
    const root = document.querySelector('ona-measuring-page,ona-curtain-calculator');
    const form = root?.querySelector('[data-guide-form]');
    if (!form) return;
    const query = new URLSearchParams(location.search);
    const fields = form.elements;
    const number = value => {
      const n = Number(value);
      return Number.isFinite(n) && n > 0 ? n : null;
    };

    const wanted = query.get('color') || query.get('shade');
    const colour = fields.color;
    if (wanted && colour && colour.tagName === 'SELECT') {
      const target = norm(wanted);
      const hit = [...colour.options].find(option => norm(option.value) === target)
        || [...colour.options].find(option => norm(option.value).startsWith(target));
      if (hit && colour.value !== hit.value) {
        colour.value = hit.value;
        colour.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }

    const drop = number(query.get('drop')) || number(query.get('length'));
    if (drop && fields.quickDrop && !fields.quickDrop.value) {
      fields.quickDrop.value = String(drop);
      fields.quickDrop.dispatchEvent(new Event('input', { bubbles: true }));
    }

    const back = root.querySelector('[data-guide-back]');
    const product = query.get('product');
    if (back && product && /^[a-z0-9-]+$/i.test(product)) {
      const url = new URL('/products/' + encodeURIComponent(product), location.origin);
      // ona-size-split's open-on-longest rule overrides a bare ?variant= on an
      // internal click, so the size is sent as width + length as well.
      for (const key of ['variant', 'color', 'width', 'length', 'quantity']) {
        const value = query.get(key);
        if (value) url.searchParams.set(key, value);
      }
      back.setAttribute('href', url.pathname + url.search);
      const shade = query.get('color'), width = query.get('width'), length = query.get('length');
      const spec = [shade, width && length ? width + '\u2033 \u00d7 ' + length + '\u2033' : ''].filter(Boolean).join(' \u00b7 ');
      back.textContent = spec ? '\u2190 Back to ' + spec : '\u2190 Back to your curtains';
    }
  }

  let frame = null;
  const schedule = () => {
    if (frame != null) return;
    frame = requestAnimationFrame(() => { frame = null; refresh(); hydrateGuide(); });
  };

  const start = () => {
    // The colour is applied before ona-size-split resolves ?width/?length, which it
    // schedules on load. On a partial matrix (velvet) a length that does not exist in
    // the default colour otherwise gets resolved against the wrong colour, and the
    // size controls and the gallery's availability filter then fight over it.
    applyColor();
    schedule();
    const info = document.querySelector('product-info');
    if (info) {
      new MutationObserver(schedule).observe(info, {
        childList: true, subtree: true, attributes: true, attributeFilter: ['value', 'checked', 'selected', 'disabled']
      });
      info.addEventListener('change', schedule, true);
    }
    window.addEventListener('popstate', schedule);
    // ona-size-split commits ?width/?length on load. The colour is applied after
    // that, never during it, so the two never fight over one Dawn re-render.
    window.addEventListener('load', () => {
      requestAnimationFrame(() => requestAnimationFrame(() => { applyColor(); schedule(); }));
      setTimeout(() => { applyColor(); schedule(); }, 600);
    }, { once: true });
    // product-info can upgrade after DOMContentLoaded; retry the colour until the
    // swatches exist, so the early pass is not lost on a slow render.
    if (window.customElements?.whenDefined) {
      customElements.whenDefined('product-info').then(() => { applyColor(); schedule(); }).catch(() => {});
    }
  };

  // Rewrite the href just before the browser follows it, so a link rendered or
  // re-rendered after the last refresh still carries the current selection.
  document.addEventListener('click', event => {
    if (event.defaultPrevented) return;
    const link = event.target.closest('a[href]');
    if (link) {
      const url = guideURL(link.getAttribute('href'));
      if (url) link.setAttribute('href', url.pathname + url.search);
      return;
    }
    const trigger = event.target.closest('[data-ona-measure-open],[data-action="measure"]');
    if (!trigger || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    const url = guideURL(GUIDE);
    if (!url) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    location.assign(url.href);
  }, true);

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, { once: true });
  else start();
  if (window.customElements?.whenDefined) customElements.whenDefined('product-info').then(schedule).catch(() => {});
})();
